import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';
import { playRingtone } from '../utils/ringtone';
import KitchenTicket from '../components/KitchenTicket';

function fmtDate(iso) {
  if (!iso) return '–';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function Card({ children, style }) {
  return <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }}>{children}</div>;
}

const RES_STATUS = {
  pending:   { label: 'Pending',   bg: '#FEF3C7', color: '#92400E', next: 'confirmed', nextLabel: 'Confirm ✓' },
  confirmed: { label: 'Confirmed', bg: '#DBEAFE', color: '#1E40AF', next: null,        nextLabel: null         },
};

const GRID = '1fr 100px 80px 150px 100px 1fr 36px';

export default function Reservations() {
  const { restaurant } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState('upcoming');
  const [printTicket, setPrintTicket]   = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() =>
    localStorage.getItem('tc_sound_alerts') === 'true'
  );
  const prevCountRef = useRef(null);

  // ── LOAD ──────────────────────────────────────────────────────────────────
  const load = useCallback(async (silent = false) => {
    if (!restaurant?.id) return;
    if (!silent) setLoading(true);
    const data = await portalFetch(`/reservations?filter=${filter}`);
    if (data?.reservations) {
      const newRes = data.reservations;

      // Sound alert — compare pending count
      if (silent && soundEnabled && prevCountRef.current !== null) {
        const activeNow  = newRes.filter(r => r.kitchen_status === 'pending' || !r.kitchen_status).length;
        const activePrev = prevCountRef.current;
        if (activeNow > activePrev) {
          const diff = activeNow - activePrev;
          playRingtone();
          if (document.hidden && Notification.permission === 'granted') {
            new Notification(`${diff} new reservation${diff > 1 ? 's' : ''} at ${restaurant.name}`, {
              body: 'Check the TableChat portal',
              icon: '/favicon.ico',
            });
          }
        }
        prevCountRef.current = activeNow;
      } else if (prevCountRef.current === null) {
        prevCountRef.current = newRes.filter(r => r.kitchen_status === 'pending' || !r.kitchen_status).length;
      }

      setReservations(newRes);
    }
    if (!silent) setLoading(false);
  }, [restaurant?.id, filter, soundEnabled]);

  useEffect(() => {
    prevCountRef.current = null;
    load();
  }, [load]);

  useEffect(() => {
    const interval = setInterval(() => load(true), 30000);
    return () => clearInterval(interval);
  }, [load]);

  // ── STATUS UPDATE — fixed: removed erroneous () after options object ──────
  const updateStatus = async (resId, newStatus) => {
    // Optimistic update
    setReservations(prev => prev.map(r =>
      r.id === resId ? { ...r, kitchen_status: newStatus } : r
    ));
    // API call — was broken by }() which called the options object as a function
    await portalFetch('/ticket-status', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'reservation', id: resId, status: newStatus }),
    });
  };

  // ── SOUND TOGGLE ──────────────────────────────────────────────────────────
  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('tc_sound_alerts', String(next));
    if (next) {
      playRingtone();
      if (Notification.permission === 'default') Notification.requestPermission();
    }
  };

  // ── SORT: pending first, confirmed at bottom ─────────────────────────────
  const sorted = [...reservations].sort((a, b) => {
    const aDone = a.kitchen_status === 'confirmed' ? 1 : 0;
    const bDone = b.kitchen_status === 'confirmed' ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    return new Date(a.reservation_date) - new Date(b.reservation_date);
  });

  const today      = new Date().toISOString().split('T')[0];
  const todayCount = reservations.filter(r => r.reservation_date === today).length;
  const totalCovers = reservations
    .filter(r => r.status !== 'cancelled')
    .reduce((s, r) => s + (r.party_size || 0), 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900, margin: 0 }}>Reservations</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>{restaurant?.name}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            onClick={toggleSound}
            title={soundEnabled ? 'Sound alerts ON — click to disable' : 'Sound alerts OFF — click to enable'}
            style={{
              padding: '0.5rem 1rem', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
              background: soundEnabled ? '#FEF3C7' : 'white',
              color: soundEnabled ? '#92400E' : 'var(--muted)',
              border: soundEnabled ? '1px solid #F59E0B' : '1px solid var(--border)',
            }}
          >
            {soundEnabled ? '🔔 Alerts On' : '🔕 Alerts Off'}
          </button>
          <button onClick={() => load()} style={{ background: 'var(--warm)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.5rem 1.1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', color: 'var(--ink)' }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total',  value: reservations.length },
          { label: 'Today',  value: todayCount },
          { label: 'Covers', value: totalCovers },
        ].map(({ label, value, color }) => (
          <Card key={label} style={{ padding: '1.2rem 1.4rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>{label}</div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.7rem', fontWeight: 900, color: color || 'var(--orange)' }}>{value}</div>
          </Card>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[['today', 'Today'], ['upcoming', 'Upcoming'], ['past', 'Past'], ['all', 'All']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: '0.5rem 1.1rem', borderRadius: 8, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
            background: filter === val ? 'var(--orange)' : 'white',
            color: filter === val ? 'white' : 'var(--mid)',
            border: filter === val ? '1px solid var(--orange)' : '1px solid var(--border)',
          }}>{label}</button>
        ))}
      </div>

      <Card>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>Loading reservations...</div>
        ) : reservations.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>📅</div>
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No reservations for this period.</p>
          </div>
        ) : (
          <div>
            {/* Header row */}
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem', padding: '0.75rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--warm)', borderRadius: '12px 12px 0 0' }}>
              {['Guest', 'Date', 'Time', 'Status', 'Action', 'Notes', '🖨'].map(h => (
                <div key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
              ))}
            </div>

            {sorted.map(res => {
              const isDone    = res.kitchen_status === 'confirmed';
              const statusCfg = RES_STATUS[res.kitchen_status] || RES_STATUS.pending;

              return (
                <div
                  key={res.id}
                  style={{
                    display: 'grid', gridTemplateColumns: GRID, gap: '0.75rem',
                    padding: '1rem 1.4rem', borderBottom: '1px solid var(--warm)',
                    alignItems: 'center', fontSize: '0.875rem',
                    background: isDone ? '#FAFAFA' : 'white',
                    opacity: isDone ? 0.65 : 1,
                  }}
                  onMouseEnter={e => { if (!isDone) e.currentTarget.style.background = 'var(--cream)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isDone ? '#FAFAFA' : 'white'; }}
                >
                  {/* Guest */}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: isDone ? 'var(--muted)' : 'var(--dark)' }}>
                      {res.customer_name || 'Guest'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 1 }}>
                      Party of {res.party_size}{res.customer_phone && ` · ${res.customer_phone}`}
                    </div>
                    {res.customer_email && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 1 }}>{res.customer_email}</div>
                    )}
                  </div>

                  {/* Date */}
                  <div style={{ fontSize: '0.875rem', color: 'var(--mid)' }}>{fmtDate(res.reservation_date)}</div>

                  {/* Time */}
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{res.reservation_time}</div>

                  {/* Status badge */}
                  <div>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 10,
                      background: statusCfg.bg, color: statusCfg.color,
                    }}>{statusCfg.label}</span>
                  </div>

                  {/* Action button */}
                  {statusCfg.next ? (
                    <div>
                      <button
                        onClick={() => updateStatus(res.id, statusCfg.next)}
                        style={{
                          fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.55rem',
                          borderRadius: 8, cursor: 'pointer', border: 'none',
                          background: statusCfg.next === 'confirmed' ? '#DBEAFE' : '#D1FAE5',
                          color: statusCfg.next === 'confirmed' ? '#1E40AF' : '#065F46',
                        }}
                      >
                        {statusCfg.nextLabel}
                      </button>
                    </div>
                  ) : <div />}

                  {/* Notes */}
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)', fontStyle: res.special_requests && res.special_requests !== 'none' ? 'italic' : 'normal' }}>
                    {res.special_requests && res.special_requests !== 'none' ? res.special_requests : '–'}
                  </div>

                  {/* Print button */}
                  <div style={{ textAlign: 'center' }}>
                    <button
                      title="Print kitchen ticket"
                      onClick={() => setPrintTicket({
                        type: 'reservation',
                        ticketNumber: `TC-RES-${res.id?.slice(-6).toUpperCase()}`,
                        customerName: res.customer_name || 'Guest',
                        customerPhone: res.customer_phone || '',
                        timestamp: new Date(res.created_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
                        partySize: res.party_size,
                        reservationDate: fmtDate(res.reservation_date),
                        reservationTime: res.reservation_time,
                        specialRequests: res.special_requests,
                      })}
                      style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', fontSize: '0.9rem' }}
                    >🖨</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {printTicket && (
        <KitchenTicket
          ticket={printTicket}
          onClose={() => setPrintTicket(null)}
        />
      )}
    </div>
  );
}
